import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import AppLink from '../util/AppLink'
import { brandColors, serif } from './brand'
import { channelMenuItems, renderChannel, teamPhoto } from './teamsGroups'
import { TeamsStatus, TeamsUsage, ago, teamsApi } from './api'
import BotAdmin from './BotAdmin'

// The Power Automate trigger's request body schema (what GoAlert sends).
const triggerSchema = JSON.stringify(
  {
    type: 'object',
    properties: {
      teamId: { type: 'string' },
      channelId: { type: 'string' },
      card: { type: 'string' },
    },
  },
  null,
  2,
)

function Section(props: {
  title: string
  subtitle?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
}): React.JSX.Element {
  const c = brandColors[useTheme().palette.mode]
  return (
    <Card sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            component='h2'
            sx={{ fontFamily: serif, fontSize: 20, color: c.ink }}
          >
            {props.title}
          </Typography>
          {props.subtitle && (
            <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.25 }}>
              {props.subtitle}
            </Typography>
          )}
        </Box>
        {props.action}
      </Box>
      {props.children}
    </Card>
  )
}

function UsedBy({ usedBy }: { usedBy: TeamsUsage[] }): React.JSX.Element {
  if (!usedBy.length) {
    return <Typography sx={{ fontSize: 13 }}>Not used</Typography>
  }
  return (
    <Stack direction='row' spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {usedBy.map((u) => (
        <Chip
          key={u.kind + u.id + (u.step ?? '')}
          size='small'
          variant='outlined'
          component={AppLink}
          to={
            u.kind === 'policy'
              ? `/escalation-policies/${u.id}`
              : `/schedules/${u.id}/on-call-notifications`
          }
          clickable
          label={u.kind === 'policy' ? `${u.name} · step ${u.step}` : u.name}
        />
      ))}
    </Stack>
  )
}

// TeamsAdmin is Admin > Microsoft Teams: the posting flow, a test sender, the
// Teams channels (straight from Microsoft Teams) and where they are used.
export default function TeamsAdmin(): React.JSX.Element {
  const c = brandColors[useTheme().palette.mode]
  const [st, setSt] = useState<TeamsStatus>()
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [flowURL, setFlowURL] = useState('')
  const [showHow, setShowHow] = useState(false)
  const [testChan, setTestChan] = useState('')
  const [testResult, setTestResult] = useState<{
    ok: boolean
    detail: string
  }>()
  const [moveTo, setMoveTo] = useState<Record<string, string>>({})

  const load = useCallback((refresh = false) => {
    teamsApi
      .status(refresh)
      .then((s) => {
        setSt(s)
        setErr('')
      })
      .catch((e: Error) => setErr(e.message))
  }, [])
  useEffect(() => load(), [load])

  const run = (fn: () => Promise<unknown>): void => {
    setBusy(true)
    setErr('')
    fn()
      .then(() => load())
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false))
  }

  const channelOptions = st?.channels ?? []
  const used = channelOptions.filter((ch) => ch.usedBy.length)

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Typography
        component='h1'
        sx={{ fontFamily: serif, fontSize: 30, color: c.ink }}
      >
        Microsoft Teams
      </Typography>
      <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
        Alerts post as cards into Teams channels. Teams and channels come
        straight from Microsoft Teams; pick one in an escalation policy step
        (“Microsoft Teams”) or a schedule’s on-call notifications.
      </Typography>

      {err && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <Stack spacing={2}>
        <BotAdmin />
        <Section
          title='Posting flow'
          subtitle='One Power Automate flow posts every card, to whichever channel the alert is for.'
          action={
            <Chip
              size='small'
              color={st?.flowConfigured ? 'success' : 'warning'}
              variant='outlined'
              label={st?.flowConfigured ? 'Set up' : 'Not set up'}
            />
          }
        >
          {st?.flow && (
            <Typography sx={{ fontSize: 14, mb: 1.5 }}>
              Current: {st.flow}
              {st.flowUpdatedAt && ` · saved ${ago(st.flowUpdatedAt)} ago`}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size='small'
              label={st?.flowConfigured ? 'Replace flow URL' : 'Flow URL'}
              placeholder='https://….powerplatform.com/powerautomate/automations/direct/workflows/…'
              value={flowURL}
              onChange={(e) => setFlowURL(e.target.value)}
              helperText='The HTTP POST URL of the flow. It contains a secret; it is never shown again after saving.'
            />
            <Button
              variant='contained'
              disabled={busy || !flowURL.trim()}
              onClick={() =>
                run(() => teamsApi.setFlow(flowURL).then(() => setFlowURL('')))
              }
              sx={{ height: 40, flexShrink: 0 }}
            >
              Save
            </Button>
          </Box>
          <Button
            size='small'
            onClick={() => setShowHow(!showHow)}
            sx={{ mt: 1 }}
          >
            {showHow ? 'Hide' : 'How to create the flow'}
          </Button>
          <Collapse in={showHow}>
            <Box
              component='ol'
              sx={{ fontSize: 14, color: c.ink, pl: 2.5, '& li': { mb: 1 } }}
            >
              <li>
                Open{' '}
                <Link
                  href='https://make.powerautomate.com'
                  target='_blank'
                  rel='noreferrer'
                >
                  Power Automate
                </Link>{' '}
                as an account that is a member of the teams you will post to.
                Create › Instant cloud flow › trigger{' '}
                <b>When an HTTP request is received</b>. Who can trigger:
                Anyone.
              </li>
              <li>
                Request Body JSON Schema:
                <Box
                  component='pre'
                  sx={{
                    m: '6px 0',
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: c.bg,
                    fontSize: 12,
                  }}
                >
                  {triggerSchema}
                </Box>
              </li>
              <li>
                Add action{' '}
                <b>Microsoft Teams › Post card in a chat or channel</b>: Post as{' '}
                <i>Flow bot</i>, Post in <i>Channel</i>, Team ={' '}
                <code>teamId</code>, Channel = <code>channelId</code> (both as
                custom values from the trigger), Adaptive Card ={' '}
                <code>card</code>.
              </li>
              <li>
                Save, copy the trigger’s <b>HTTP URL</b>, paste it above, then
                send a test below.
              </li>
            </Box>
          </Collapse>
        </Section>

        <Section
          title='Send a test'
          subtitle='Posts a test card through the flow into the channel you pick.'
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <TextField
              select
              size='small'
              label='Channel'
              SelectProps={{ renderValue: renderChannel(channelOptions) }}
              value={testChan}
              onChange={(e) => setTestChan(e.target.value)}
              sx={{ minWidth: 320, flex: 1 }}
            >
              {channelMenuItems(channelOptions)}
            </TextField>
            <Button
              variant='outlined'
              disabled={busy || !testChan || !st?.flowConfigured}
              onClick={() =>
                run(() => teamsApi.test(testChan).then(setTestResult))
              }
            >
              Send test card
            </Button>
          </Box>
          {(testResult || st?.lastTestAt) && (
            <Alert
              severity={
                (testResult?.ok ?? st?.lastTestOK) ? 'success' : 'error'
              }
              sx={{ mt: 1.5 }}
            >
              {(testResult?.ok ?? st?.lastTestOK)
                ? 'The flow accepted the card. Check the channel.'
                : `The flow did not accept the card: ${testResult?.detail || st?.lastTestDetail}`}
            </Alert>
          )}
        </Section>

        <Section
          title='Channels in use'
          subtitle='Teams channels that escalation policies or schedules post to.'
          action={
            <Button size='small' onClick={() => load(true)}>
              Reload from Teams
            </Button>
          }
        >
          {st?.graphError && (
            <Alert severity='warning' sx={{ mb: 1.5 }}>
              Could not read Microsoft Teams: {st.graphError}
            </Alert>
          )}
          {st && !used.length && (
            <Typography sx={{ fontSize: 14, color: c.muted }}>
              No escalation policy or schedule posts to Teams yet. In a policy,
              add a step with destination “Microsoft Teams” and pick the
              channel. {channelOptions.length} channels are available.
            </Typography>
          )}
          <Stack divider={<Divider flexItem />}>
            {used.map((ch) => (
              <Box
                key={ch.value}
                sx={{
                  py: 1,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <Avatar
                  variant='rounded'
                  src={teamPhoto(ch.value)}
                  alt=''
                  sx={{ width: 36, height: 36 }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    {ch.webUrl ? (
                      <Link
                        href={ch.webUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        underline='hover'
                        color='inherit'
                        title='Open in Microsoft Teams'
                      >
                        {ch.team} › {ch.channel}
                      </Link>
                    ) : (
                      <React.Fragment>
                        {ch.team} › {ch.channel}
                      </React.Fragment>
                    )}
                  </Typography>
                  <UsedBy usedBy={ch.usedBy} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Section>

        {st && st.webhooks.length > 0 && (
          <Section
            title='Move old webhooks to Teams'
            subtitle='These steps still use a plain webhook to a Power Automate flow. Moving one sends Teams cards through the posting flow instead, to the channel you pick.'
          >
            <Stack divider={<Divider flexItem />}>
              {st.webhooks.map((w) => (
                <Box key={w.id} sx={{ py: 1.5 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    Webhook: {w.workflow}
                  </Typography>
                  <UsedBy usedBy={w.usedBy} />
                  <Box
                    sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}
                  >
                    <TextField
                      select
                      size='small'
                      label='Move to channel'
                      SelectProps={{
                        renderValue: renderChannel(channelOptions),
                      }}
                      value={moveTo[w.id] ?? ''}
                      onChange={(e) =>
                        setMoveTo({ ...moveTo, [w.id]: e.target.value })
                      }
                      sx={{ minWidth: 320, flex: 1 }}
                    >
                      {channelMenuItems(channelOptions)}
                    </TextField>
                    <Button
                      variant='outlined'
                      disabled={busy || !moveTo[w.id] || !st.flowConfigured}
                      onClick={() =>
                        run(() => teamsApi.convert(w.id, moveTo[w.id]))
                      }
                    >
                      Move to Teams
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
            {!st.flowConfigured && (
              <Typography sx={{ fontSize: 13, color: c.muted, mt: 1 }}>
                Set up the posting flow first.
              </Typography>
            )}
          </Section>
        )}
      </Stack>
    </Box>
  )
}
